
import { Draggable } from "@hello-pangea/dnd";
import { Edit2, Trash2, GripVertical, Play, Pause, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EventFlow } from "@/hooks/useEventManagement";
import { BookingStatus } from "@/hooks/useBookingStatuses";

// Helper functions for status display
const getStatusColor = (statusKey: string, statuses?: BookingStatus[]) => {
  return statuses?.find(s => s.status_key === statusKey)?.color || '#6b7280';
};

const getStatusDisplayName = (statusKey: string, statuses?: BookingStatus[]) => {
  return statuses?.find(s => s.status_key === statusKey)?.display_name || statusKey;
};

interface EventFlowItemProps {
  flow: EventFlow;
  index: number;
  onEdit: (flowId: string) => void;
  onDelete: (flowId: string) => void;
  onToggleActive: (flowId: string, currentActive: boolean) => void;
  reordering: boolean;
  hasUnsupportedChannels?: boolean;
  unsupportedChannels?: string[];
  statuses?: BookingStatus[]; // NEW: Pass statuses for display
}

export function EventFlowItem({ 
  flow, 
  index, 
  onEdit, 
  onDelete, 
  onToggleActive, 
  reordering,
  hasUnsupportedChannels,
  unsupportedChannels,
  statuses
}: EventFlowItemProps) {
  return (
    <TooltipProvider>
      <Draggable
      key={flow.id} 
      draggableId={flow.id} 
      index={index} 
      isDragDisabled={reordering}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center justify-between p-4 border rounded-lg bg-card transition-all ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
          } ${reordering ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-4">
            <div 
              {...provided.dragHandleProps} 
              className={reordering ? 'cursor-not-allowed' : 'cursor-grab'}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            
            <div className="flex-1">
              <div className="font-medium">{flow.name}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                {flow.from_status_info ? (
                  <Badge variant="outline" style={{ borderColor: flow.from_status_info.color }}>
                    {flow.from_status_info.display_name}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    Any Status
                  </Badge>
                )}
                
                <span>→</span>
                
                {/* Show all target statuses */}
                <div className="flex flex-wrap gap-1">
                  {flow.target_statuses && flow.target_statuses.length > 0 ? (
                    flow.target_statuses.length <= 3 ? (
                      // Show all badges if 3 or fewer
                      flow.target_statuses.map((statusKey, idx) => (
                        <Badge
                          key={idx}
                          style={{
                            backgroundColor: getStatusColor(statusKey, statuses),
                            color: 'white'
                          }}
                        >
                          {getStatusDisplayName(statusKey, statuses)}
                        </Badge>
                      ))
                    ) : (
                      // Show count with tooltip if more than 3
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary">
                            {flow.target_statuses.length} statuses
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div className="flex flex-wrap gap-1">
                            {flow.target_statuses.map((statusKey, idx) => (
                              <Badge
                                key={idx}
                                style={{
                                  backgroundColor: getStatusColor(statusKey, statuses),
                                  color: 'white'
                                }}
                                className="text-xs"
                              >
                                {getStatusDisplayName(statusKey, statuses)}
                              </Badge>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )
                  ) : (
                    // Fallback to old to_status for backward compatibility
                    flow.to_status_info && (
                      <Badge style={{ backgroundColor: flow.to_status_info.color, color: 'white' }}>
                        {flow.to_status_info.display_name}
                      </Badge>
                    )
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline">
                  {(flow as any).template?.name || flow.template_category || 'Unknown Template'}
                </Badge>
                {hasUnsupportedChannels && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Channel Issue
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">
                        This flow uses {unsupportedChannels?.join(", ")} but the template no longer supports {unsupportedChannels?.length === 1 ? 'it' : 'them'}.
                        Edit the flow to fix this issue.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
                <div className="flex flex-wrap gap-1">
                  {flow.channels.map((channel) => (
                    <Badge 
                      key={channel} 
                      variant={unsupportedChannels?.includes(channel) ? "destructive" : "secondary"}
                      className="text-xs"
                    >
                      {channel}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline">Priority: {flow.priority}</Badge>
            {flow.delay_minutes > 0 ? (
              <span className="text-sm text-muted-foreground">{flow.delay_minutes}m</span>
            ) : (
              <span className="text-muted-foreground text-sm">Immediate</span>
            )}
            
            <div className="flex items-center gap-2">
              <Badge variant={flow.is_active ? "default" : "secondary"}>
                {flow.is_active ? "Active" : "Inactive"}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleActive(flow.id, flow.is_active)}
                disabled={reordering}
                className="h-6 w-6 p-0"
              >
                {flow.is_active ? (
                  <Pause className="h-3 w-3" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
              </Button>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={reordering}>
                  <Edit2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(flow.id)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(flow.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </Draggable>
    </TooltipProvider>
  );
}
